import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { Button } from "@src/appV2/redesign/components/Button";
import { StickyFooter } from "@src/appV2/redesign/components/StickyFooter";
import { useCallback } from "react";
import { useHistory } from "react-router-dom";

import { useInHomeVisitModalsDataContext } from "../../ShiftDiscovery/useInHomeVisitModalsDataContext";
import { VisitPayInfoLabel } from "../Card/PayInfoLabel";
import {
  BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH,
  BOOKED_VISIT_DROP_MODAL_PATH,
  BOOKED_VISIT_LOG_DATE_MODAL_PATH,
  BOOKED_VISIT_PEND_MODAL_PATH,
} from "../paths";
import { type VisitStatus } from "../types";
import { AccessLockedButton } from "../VisitBottomSheet/AccessLockedButton";

function isVisitStatusConfirmable(status: VisitStatus): boolean {
  return status === "FILLED" || status === "PENDING";
}

function isVisitStatusPendable(status: VisitStatus): boolean {
  return status === "FILLED" || status === "CONFIRMED";
}

function isVisitStatusLoggable(status: VisitStatus): boolean {
  return status === "CONFIRMED" || status === "PENDING" || status === "LOGGED";
}

function isVisitStatusDroppable(status: VisitStatus): boolean {
  return (
    status === "FILLED" || status === "CONFIRMED" || status === "PENDING" || status === "LOGGED"
  );
}

interface BookedVisitDetailsFooterProps {
  lockedByWorkplaceId?: string;
  isLoading: boolean;
}

export function BookedVisitDetailsFooter(props: BookedVisitDetailsFooterProps) {
  const { lockedByWorkplaceId, isLoading } = props;
  const history = useHistory();
  const { visit, generateModalPath } = useInHomeVisitModalsDataContext();
  const { attributes: visitAttributes } = visit ?? {};
  const { status } = visitAttributes ?? {};

  const isLocked = isDefined(lockedByWorkplaceId);

  const handleDropVisit = useCallback(() => {
    if (!isDefined(visit)) {
      return;
    }

    history.push(
      generateModalPath(BOOKED_VISIT_DROP_MODAL_PATH, {
        caseId: visit.attributes.caseId,
        visitId: visit.id,
      })
    );
  }, [visit, history, generateModalPath]);

  const dropVisitButton = isDefined(status) && isVisitStatusDroppable(status) && (
    <Button fullWidth variant="destructive" onClick={handleDropVisit}>
      Drop Visit
    </Button>
  );

  return (
    <StickyFooter>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ paddingX: 2, marginBottom: 5 }}
      >
        {isDefined(visit) && (
          <VisitPayInfoLabel
            visit={visit}
            wrapperSx={{ pl: 5 }}
            textVariant="h4"
            totalPayTextVariant="h4"
          />
        )}
      </Stack>
      {isLocked ? (
        <Stack paddingBottom={4} direction="row" spacing={2} alignItems="center">
          <AccessLockedButton
            fullWidth
            size="medium"
            lockedByWorkplaceId={lockedByWorkplaceId}
            source="booked-visit-details"
          />
          {dropVisitButton}
        </Stack>
      ) : (
        <Stack paddingBottom={4} direction="row" spacing={2} alignItems="center">
          {isDefined(status) && isVisitStatusPendable(status) && (
            <Button
              fullWidth
              variant="outlined"
              disabled={isLoading}
              onClick={() => {
                if (!isDefined(visit)) {
                  return;
                }

                history.push(
                  generateModalPath(BOOKED_VISIT_PEND_MODAL_PATH, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            >
              Pend
            </Button>
          )}
          {isDefined(status) && isVisitStatusLoggable(status) && (
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading}
              onClick={() => {
                if (!isDefined(visit)) {
                  return;
                }

                history.push(
                  generateModalPath(BOOKED_VISIT_LOG_DATE_MODAL_PATH, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            >
              Log
            </Button>
          )}
          {isDefined(status) && isVisitStatusConfirmable(status) && (
            <Button
              fullWidth
              variant="contained"
              disabled={isLoading}
              onClick={() => {
                if (!isDefined(visit)) {
                  return;
                }

                history.push(
                  generateModalPath(BOOKED_VISIT_CONFIRM_DATE_MODAL_PATH, {
                    caseId: visit.attributes.caseId,
                    visitId: visit.id,
                  })
                );
              }}
            >
              Confirm
            </Button>
          )}
          {dropVisitButton}
        </Stack>
      )}
    </StickyFooter>
  );
}
